import { useMutation, useQuery } from '@apollo/client';
import { message } from 'antd';
import { FC } from 'react';
import {
  CasesCredentialVerificationV1ChatPageContainerCredentialVerificationCaseV1Document,
  CasesCredentialVerificationV1ChatPageContainerCredentialVerificationCaseV1FieldsFragment,
  CasesCredentialVerificationV1ChatPageContainerCredentialVerificationCaseV1UpdateDocument,
  CasesCredentialVerificationV1ChatPageContainerStaffUserCurrentDocument,
  CredentialVerificationCaseV1UpdateInput
} from '../../../../../../generated';
import { useOid } from '../../../../../../hooks/useOid';
import ChatPage, { CaseData } from '../../../../../shared/chat/chat-page';
import { ComponentQueryLoader } from '../../../../../shared/component-query-loader';
import { getStaffUserCurrentQueryOptionsWithOidAndCacheDisabled } from '../../../../../shared/utils/queryOptions';

const requestObject = {
  requestUpdatedCredentialDetails: false,
  requestUpdatedIssuingInstitution: false,
  requestUploadCredential: false,
  requestUploadTranslation: false
};

interface ChatPageContainerProps {
  caseId: string;
  isCaseOrApplicant: string;
}

export const ChatPageContainer: FC<ChatPageContainerProps> = (props) => {
  const oid = useOid();
const staffUserCurrentQueryOptions = getStaffUserCurrentQueryOptionsWithOidAndCacheDisabled(oid);

  const {
    data: queryData,
    loading,
    error
  } = useQuery(CasesCredentialVerificationV1ChatPageContainerCredentialVerificationCaseV1Document, {
    variables: {
      credentialVerificationCaseV1Id: props.caseId
    }
  });

  const [updatedMessages] = useMutation(CasesCredentialVerificationV1ChatPageContainerCredentialVerificationCaseV1UpdateDocument);

  const {
    data: staffData,
    loading: staffLoading,
    error: staffError
  } = useQuery(CasesCredentialVerificationV1ChatPageContainerStaffUserCurrentDocument, staffUserCurrentQueryOptions);

  const updateEmbeddingWithAdhocTransactions = async (data: CasesCredentialVerificationV1ChatPageContainerCredentialVerificationCaseV1FieldsFragment, values: any) => {
    const adhocTransaction = data?.financeDetails?.transactions?.adhocTransactions?.find((transaction) => {
      return (
        transaction?.reason === values?.financeDetails?.transactions?.adhocTransactions[0]?.reason &&
        transaction?.amount === values?.financeDetails?.transactions?.adhocTransactions[0]?.amount &&
        transaction?.approval?.isApplicantApproved === null
      );
    });
    const messageId = data?.messages?.find((message: any) => {
      return message?.embedding === values?.messages[0]?.embedding;
    });
    const embeddingArray = JSON.parse(values?.messages[0]?.embedding);
    let updatedEmbedding = embeddingArray.find((data: any) => {
      return (
        data.adhocPayment.reason === values?.financeDetails?.transactions?.adhocTransactions[0]?.reason &&
        data.adhocPayment.amount === values?.financeDetails?.transactions?.adhocTransactions[0]?.amount
      );
    });
    updatedEmbedding['adhocTransactionId'] = adhocTransaction?.id;

    await updatedMessages({
      variables: {
        input: {
          id: props.caseId,
          messages: [
            {
              id: messageId?.id,
              sentBy: 'internal',
              message: values?.messages[0]?.message,
              embedding: JSON.stringify(embeddingArray)
            }
          ]
        }
      }
    });
  };

  const handleSave = async (values: any) => {
    let inputData: CredentialVerificationCaseV1UpdateInput = {
      id: props.caseId,
      messages: values?.messages
    };

    if (values?.revisionRequest) {
      inputData.revisionRequest = {
        ...values?.revisionRequest
      };
    }

    if (values?.financeDetails) {
      inputData.financeDetails = {
        ...values?.financeDetails
      };
    }
    if (values?.adhocPayment) {
      inputData.adhocPayment = {
        ...values?.adhocPayment
      };
    }
    try {
      const result = await updatedMessages({
        variables: {
          input: inputData
        }
      });
      if (result.data?.credentialVerificationCaseV1Update?.status.success) {
        if (result.data?.credentialVerificationCaseV1Update.credentialVerificationCaseV1) {
          try {
            if (values?.financeDetails) {
              await updateEmbeddingWithAdhocTransactions(result.data?.credentialVerificationCaseV1Update?.credentialVerificationCaseV1, values);
            }
          } catch (error) {
            console.error('Error parsing adhoc transaction details ', error);
          }
          message.success('Message sends successfully');
        }
      } else {
        message.error(`Error in sending message: ${result.data?.credentialVerificationCaseV1Update?.status.errorMessage}`);
      }
    } catch (error) {
      message.error(`Error in sending message: ${JSON.stringify(error)}`);
    }
  };

  return (
    <ComponentQueryLoader
      error={error || staffError}
      loading={loading || staffLoading}
      hasData={queryData && staffData}
      hasDataComponent={
        <ChatPage
          caseData={queryData?.credentialVerificationCaseV1 as CaseData}
          requestChangesFields={requestObject}
          onSave={handleSave}
          updateMessage={() => {}}
          canIssueRefund={staffData?.staffUserCurrent?.role?.permissions.credCasePermissions.canIssueRefunds}
          isCaseOrApplicant={props.isCaseOrApplicant}
        />
      }
    />
  );
};
